<template>
  <!-- Table -->
  <DataView ref="dataViewRef" v-bind="tableConfigRef">
    <!-- activo column -->
    <template #table__activo__body="{ data }">
      <Badge v-if="data.activo" value="Activo" severity="success"></Badge>
      <Badge v-else value="Inactivo" severity="danger"></Badge>
    </template>
    <template #list__activo="{ data }">
      <Badge v-if="data.activo" value="Activo" severity="success"></Badge>
      <Badge v-else value="Inactivo" severity="danger"></Badge>
    </template>

    <!-- nombre column -->
    <template #table__nombre__body="{ data }">
      <div v-if="data.nombre">{{ data.nombre }} {{ data.apellido1 }}</div>
      <div v-else class="text-gray-400">-</div>
    </template>
    <template #list__nombre="{ data }">
      <div v-if="data.nombre">{{ data.nombre }} {{ data.apellido1 }}</div>
      <div v-else class="text-gray-400">-</div>
    </template>

    <!-- origen column -->
    <template #table__esInterno__body="{ data }">
      <div v-if="data.esInterno">Interno</div>
      <div v-else>Externo</div>
    </template>
    <template #list__esInterno="{ data }">
      <div v-if="data.esInterno">Interno</div>
      <div v-else>Externo</div>
    </template>

    <!-- unidad column -->
    <template #table__afiliacion__body="{ data }">
      <div v-if="data.unidad">{{ data.unidad }}</div>
      <div v-else class="text-gray-400">-</div>
    </template>
    <template #list__afiliacion="{ data }">
      <div v-if="data.unidad">{{ data.unidad }}</div>
      <div v-else class="text-grey-400">-</div>
    </template>

    <!-- rol column -->
    <template #table__rol__body="{ data }">
      <Dropdown
        v-model="data.rol"
        :options="roles"
        optionLabel="label"
        optionValue="value"
        placeholder="Rol del usuario"
        @change="handleUpdateUser(data)"
        :disabled="!data.email.includes('@externomf') && false"
      />
    </template>
    <template #list__rol="{ data }">
      <Dropdown
        v-model="data.rol"
        :options="roles"
        optionLabel="label"
        optionValue="value"
        placeholder="Rol del usuario"
        @change="handleUpdateUser(data)"
        :disabled="!data.email.includes('@externomf') && false"
      />
    </template>

    <!-- custom numInformes content -->
    <template #table__numInformes__body="{ data }">
      <div
        v-if="data.rol == 'Admin' || data.numInformes > 0"
        class="text-blue-200 cursor-pointer text-primary font-bold"
        @click="showUserHasReportsModal(data)"
      >
        <div v-if="data.rol == 'Admin'">Todos</div>
        <div v-else>
          {{ data.numInformes }}
        </div>
      </div>
      <div v-else>0</div>
    </template>
    <template #list__numInformes="{ data }">
      <div
        v-if="data.rol == 'Admin' || data.numInformes > 0"
        class="text-blue-200 cursor-pointer text-primary font-bold"
        @click="showUserHasReportsModal(data)"
      >
        <div v-if="data.rol == 'Admin'">Todos</div>
        <div v-else>
          {{ data.numInformes }}
        </div>
      </div>
      <div v-else>0</div>
    </template>

    <!-- custom grupos content -->
    <template #table__grupos__body="{ data }">
      <div
        class="text-blue-200 cursor-pointer text-primary font-bold"
        @click="showUserHasGroupsModal(data)"
      >
        <span v-if="data.idGrupo.length > 0">{{ data.idGrupo.length }}</span>
        <span v-else>0</span>
      </div>
    </template>
    <template #list__grupos="{ data }">
      <div
        class="text-blue-200 cursor-pointer text-primary font-bold"
        @click="showUserHasGroupsModal(data)"
      >
        <span v-if="data.idGrupo.length > 0">{{ data.idGrupo.length }}</span>
        <span v-else>0</span>
      </div>
    </template>

    <!-- ultimoAcceso column -->
    <template #table__ultimoAcceso__body="{ data }">
      <div v-if="data.ultimoAcceso">
        {{ formatDate(convertUtcToLocalTime(data.ultimoAcceso)) }}
        {{ formatTime(convertUtcToLocalTime(data.ultimoAcceso)) }}
      </div>
      <div v-else class="text-gray-400">-</div>
    </template>
    <template #list__ultimoAcceso="{ data }">
      <div v-if="data.ultimoAcceso">
        {{ formatDate(convertUtcToLocalTime(data.ultimoAcceso)) }}
        {{ formatTime(convertUtcToLocalTime(data.ultimoAcceso)) }}
      </div>
      <div v-else class="text-gray-400">-</div>
    </template>
  </DataView>

  <!-- Modal -->
  <UsersDialogView ref="modalRef" @saved="updateTableList" />

  <!-- Update password modal -->
  <UpdatePasswordDialogView ref="updatePasswordModalRef" />

  <!-- User has reports modal -->
  <UserHasReportsDialog
    ref="userHasReportsModalRef"
    :fromUserHasReports="true"
  />

  <!-- User has groups modal -->
  <UserHasGroupsDialog
    ref="userHasGroupsModalRef"
    :user="selectedUser"
    @add-group="updateTableList()"
    @delete-group="updateTableList()"
  />
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useStore } from "vuex";
import Dropdown from "primevue/dropdown";
import Badge from "primevue/badge";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { DataView, useImmer } from "@sttedra_ui/framework";

import useTableConfig from "@/pages/_root/users/users-table.view-config.js";
import roles from "@/api/roles/roles.api.js";
import {
  appUserUpdateUserRol,
  appUserEnableUser,
  appUserDeleteUser,
} from "@/api/users/users.api.js";
import {
  formatDate,
  formatTime,
  convertUtcToLocalTime,
  disableAutocompleteForGlobalSearchInput,
} from "@/composables/_commons/helper.js";
import UsersDialogView from "@/pages/_root/users-dialog/UsersDialogView.vue";
import UpdatePasswordDialogView from "@/pages/_root/update-password-dialog/UpdatePasswordDialogView.vue"; // eslint-disable-line
import UserHasReportsDialog from "@/pages/_root/user-has-reports-dialog/UserHasReportsDialog.vue";
import UserHasGroupsDialog from "@/pages/_root/user-has-groups-dialog/UserHasGroupsDialog.vue";

// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const store = useStore();
const confirm = useConfirm();
const toast = useToast();
const dataViewRef = ref(null);
const tableConfig = new useTableConfig(
  buildModal,
  enableRegister,
  deleteRegister,
  impersonate,
  updatePassword
);
const { ref: tableConfigRef } = useImmer(tableConfig);
const modalRef = ref(null);
const updatePasswordModalRef = ref(null);
const userHasReportsModalRef = ref(null);
const userHasGroupsModalRef = ref(null);
const selectedUser = ref(null);
// #endregion

// #region Functions
function buildModal(event = null, type) {
  modalRef.value.showModal(event ? event.data : null, type);
}

function updateTableList() {
  dataViewRef.value.debounceRefreshAndUpdate();
}

async function handleUpdateUser(user) {
  await appUserUpdateUserRol({
    email: user.email,
    rol: user.rol,
  });

  updateTableList();
}

function enableRegister({ data }) {
  confirm.require({
    message: `¿Desea ${data.activo ? "desactivar" : "activar"} el usuario?`,
    header: `${data.activo ? "Desactivar" : "Activar"} usuario`,
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      const response = await appUserEnableUser(data.email);

      if (response == true || response == "") {
        toast.add({
          severity: "success",
          summary: `Usuario ${data.activo ? "desactivado" : "activado"}`,
          detail: `Se ha ${
            data.activo ? "desactivado" : "activado"
          } el usuario correctamente`,
          life: 3000,
        });

        updateTableList();
      } else {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: `Ha ocurrido un error al ${
            data.activo ? "desactivar" : "activar"
          } el usuario`,
          life: 3000,
        });
      }
    },
  });
}

function deleteRegister({ data }) {
  confirm.require({
    message: "¿Desea borrar el usuario?",
    header: "Borrar usuario",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      const response = await appUserDeleteUser(data.email);

      if (response == true || response == "") {
        toast.add({
          severity: "success",
          summary: "Usuario borrado",
          detail: `Se ha borrado el usuario correctamente`,
          life: 3000,
        });

        updateTableList();
      } else {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Ha ocurrido un error al borrar el usuario",
          life: 3000,
        });
      }
    },
  });
}

// Trigger the impersonation
function impersonate(event) {
  store.commit("setTriggerImpersonationUser", event.data);
}

// Open the update password modal with the data
function updatePassword({ data }) {
  updatePasswordModalRef.value.showModal(data.email);
}

// Open the user has reports modal with the data
function showUserHasReportsModal(data) {
  userHasReportsModalRef.value.showModal(data);
}

// Open the user has groups modal with the data
function showUserHasGroupsModal(data) {
  selectedUser.value = data;
  userHasGroupsModalRef.value.showModal();
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  disableAutocompleteForGlobalSearchInput();
});
// #endregion

// #region Provide, Expose
// #endregion
</script>
